import React, { useState, useEffect } from 'react';
import { Grid, Typography, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemText, Chip, Box, TextField, MenuItem, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { StatusCard, GapCard, AddFindingForm, EditFindingForm } from '../../components/gap-analysis';
import gapAnalysisService, { GapControl, GapStats } from '../../services/gapAnalysisService';
import { exportToExcel, exportToPDF, exportFilteredData } from '../../utils/gapAnalysisExporter';
import { FileDownload, PictureAsPdf, Edit } from '@mui/icons-material';
import a11yService, { A11ySummary, A11yIssue } from '../../services/a11yService';

export default function GapDashboard() {
  const [controls, setControls] = useState<GapControl[]>([]);
  const [stats, setStats] = useState<GapStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedControl, setSelectedControl] = useState<GapControl | null>(null);
  const [showAddFinding, setShowAddFinding] = useState(false);
  const [findingControlId, setFindingControlId] = useState<string>('');
  const [findingControlName, setFindingControlName] = useState<string>('');
  const [editingFinding, setEditingFinding] = useState<any>(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: ''
  });

  const [a11ySummary, setA11ySummary] = useState<A11ySummary | null>(null);
  const [a11yLoading, setA11yLoading] = useState(false);
  const [a11yError, setA11yError] = useState<string | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<string | null>(null);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issuesError, setIssuesError] = useState<string | null>(null);
  const [issues, setIssues] = useState<A11yIssue[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [controlsData, statsData] = await Promise.all([
          gapAnalysisService.getControls(),
          gapAnalysisService.getStats()
        ]);
        setControls(controlsData);
        setStats(statsData);
      } catch (err: any) {
        setError(err.message || 'Failed to load gap analysis data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const loadA11ySummary = async () => {
      try {
        setA11yLoading(true);
        setA11yError(null);
        const summary = await a11yService.getSummary('staging');
        setA11ySummary(summary);
      } catch (err) {
        setA11yError('Unable to load 508 accessibility summary.');
      } finally {
        setA11yLoading(false);
      }
    };

    loadA11ySummary();
  }, []);

  const handleOpenRouteIssues = async (route: string) => {
    try {
      setSelectedRoute(route);
      setIssueDialogOpen(true);
      setIssuesLoading(true);
      setIssuesError(null);

      const result = await a11yService.getIssues({
        route,
        status: 'open',
        environment: 'staging',
        page: 1,
        limit: 100
      });

      setIssues(result.issues);
    } catch (err) {
      setIssuesError('Unable to load issues for this page.');
    } finally {
      setIssuesLoading(false);
    }
  };

  const handleCloseRouteIssues = () => {
    setIssueDialogOpen(false);
    setSelectedRoute(null);
    setIssues([]);
    setIssuesError(null);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }
  
  return (
    <div className="gap-dashboard">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Gap Analysis Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={() => exportFilteredData(controls, filters, 'excel')}
            size="small"
          >
            Export Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdf />}
            onClick={() => exportFilteredData(controls, filters, 'pdf')}
            size="small"
            color="error"
          >
            Export PDF
          </Button>
        </Box>
      </Box>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <StatusCard 
            title="Open Findings" 
            count={stats?.openFindings || 0} 
            trend="neutral" 
            color="warning"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatusCard 
            title="Critical Gaps" 
            count={stats?.criticalFindings || 0} 
            trend="up" 
            color="error"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatusCard 
            title="Remediation Due" 
            count={stats?.remediationDue || 0} 
            trend="down" 
            color="info"
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }} component="section" aria-labelledby="a11y-gap-heading">
        <Typography id="a11y-gap-heading" variant="h5" component="h2" gutterBottom>
          508 Accessibility Gaps
        </Typography>

        {a11yLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={20} />
            <Typography>Loading accessibility data																		</Typography>
          </Box>
        )}

        {a11yError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {a11yError}
          </Alert>
        )}

        {a11ySummary && !a11yLoading && !a11yError && (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    p: 2,
                    borderRadius: 1
                  }}
                  aria-label="Total open 508 issues"
                >
                  <Typography variant="subtitle2">Open 508 Issues</Typography>
                  <Typography variant="h4">{a11ySummary.totals.open}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    bgcolor: 'secondary.main',
                    color: 'white',
                    p: 2,
                    borderRadius: 1
                  }}
                  aria-label="Critical 508 issues"
                >
                  <Typography variant="subtitle2">Critical Issues</Typography>
                  <Typography variant="h4">{a11ySummary.totals.critical}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    bgcolor: '#ff8c00',
                    color: 'white',
                    p: 2,
                    borderRadius: 1
                  }}
                  aria-label="Serious 508 issues"
                >
                  <Typography variant="subtitle2">Serious Issues</Typography>
                  <Typography variant="h4">{a11ySummary.totals.serious}</Typography>
                </Box>
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Issues by Page
            </Typography>
            <Box
              role="region"
              aria-label="508 issues by page"
              sx={{ overflowX: 'auto', mb: 2 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Page</TableCell>
                    <TableCell align="right">Open</TableCell>
                    <TableCell align="right">Critical</TableCell>
                    <TableCell align="right">Serious</TableCell>
                    <TableCell align="right">Moderate</TableCell>
                    <TableCell align="right">Minor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {a11ySummary.byRoute.map((row) => (
                    <TableRow
                      key={row.pageRoute}
                      hover
                      sx={{ cursor: 'pointer' }}
                      role="button"
                      tabIndex={0}
                      aria-label={`View 508 issues for page ${row.pageRoute}`}
                      onClick={() => handleOpenRouteIssues(row.pageRoute)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleOpenRouteIssues(row.pageRoute);
                        }
                      }}
                    >
                      <TableCell>{row.pageRoute}</TableCell>
                      <TableCell align="right">{row.open}</TableCell>
                      <TableCell align="right">{row.critical}</TableCell>
                      <TableCell align="right">{row.serious}</TableCell>
                      <TableCell align="right">{row.moderate}</TableCell>
                      <TableCell align="right">{row.minor}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Most Common 508 Violations
              </Typography>
              {a11ySummary.topRules.length === 0 ? (
                <Typography>No open violations for the selected environment.</Typography>
              ) : (
                <Box component="ul" sx={{ pl: 3 }} aria-label="Top accessibility rules violated">
                  {a11ySummary.topRules.map((rule) => (
                    <Box component="li" key={rule.ruleId} sx={{ mb: 0.5 }}>
                      <Typography variant="body2">
                        <strong>{rule.ruleId}</strong> 																		
                        																		
                        - {rule.ruleDescription} ({rule.openCount} open)
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>

      <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
        Gap Controls
      </Typography>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          select
          label="Status"
          size="small"
          value={filters.status}
          onChange={(e) => setFilters({...filters, status: e.target.value})}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Compliant">Compliant</MenuItem>
          <MenuItem value="NonCompliant">Non-Compliant</MenuItem>
        </TextField>
        
        <TextField
          select
          label="Priority"
          size="small"
          value={filters.priority}
          onChange={(e) => setFilters({...filters, priority: e.target.value})}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Critical">Critical</MenuItem>
          <MenuItem value="High">High</MenuItem>
          <MenuItem value="Medium">Medium</MenuItem>
          <MenuItem value="Low">Low</MenuItem>
        </TextField>

        <TextField
          select
          label="Category"
          size="small"
          value={filters.category}
          onChange={(e) => setFilters({...filters, category: e.target.value})}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Access">Access</MenuItem>
          <MenuItem value="Audit">Audit</MenuItem>
          <MenuItem value="Security">Security</MenuItem>
          <MenuItem value="Data">Data</MenuItem>
          <MenuItem value="Privacy">Privacy</MenuItem>
          <MenuItem value="Governance">Governance</MenuItem>
          <MenuItem value="Operations">Operations</MenuItem>
        </TextField>

        {(filters.status || filters.priority || filters.category) && (
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => setFilters({ status: '', priority: '', category: '' })}
          >
            Clear Filters
          </Button>
        )}
      </Box>

      {(() => {
        console.debug('[GAP] Applying filters', {
          activeFilters: filters,
          totalControls: controls.length,
          totalFindings: controls.flatMap(c => c.findings || []).length,
          compliantFindings: controls.flatMap(c => c.findings || []).filter(f => f.currentState === 'Compliant').length,
          nonCompliantFindings: controls.flatMap(c => c.findings || []).filter(f => f.currentState === 'NonCompliant').length
        });

        const filteredControls = controls.filter(control => {
          // Status filter: Check if control has findings matching the status
          if (filters.status) {
            const hasMatchingFindings = control.findings?.some(f => f.currentState === filters.status);
            if (!hasMatchingFindings) return false;
          }
          
          // Priority filter: Check if control has findings matching the priority
          if (filters.priority) {
            const hasMatchingPriority = control.findings?.some(f => f.priority === filters.priority);
            if (!hasMatchingPriority) return false;
          }
          
          // Category filter: Check control category
          if (filters.category && control.category !== filters.category) return false;
          
          return true;
        });

        console.debug('[GAP] Filter results', {
          filteredCount: filteredControls.length,
          filteredFindings: filteredControls.flatMap(c => c.findings || []).length
        });

        if (!filteredControls || filteredControls.length === 0) return (
        <Alert severity="info" sx={{ mt: 2 }}>
          No gap controls found. Create your first control to get started.
        </Alert>
        );

        return filteredControls.map((control) => {
          // Determine overall status based on findings
          const hasNonCompliant = control.findings?.some(f => f.currentState === 'NonCompliant');
          const hasCompliant = control.findings?.some(f => f.currentState === 'Compliant');
          const overallStatus = hasNonCompliant ? 'NonCompliant' : (hasCompliant ? 'Compliant' : 'Compliant');
          
          return (
            <GapCard 
              key={control._id}
              control={`${control.identifier}: ${control.description}`}
              status={overallStatus}
              priority={(control.severity as any) || 'Medium'}
              findings={(control.findings && Array.isArray(control.findings)) ? control.findings.length : 0}
              onClick={() => setSelectedControl(control)}
            />
          );
        });
      })()}

      {selectedControl && (
        <Dialog 
          open={!!selectedControl} 
          onClose={() => setSelectedControl(null)} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">
                {selectedControl.identifier}: {selectedControl.description}
              </Typography>
              <Chip 
                label={`${selectedControl.findings?.length || 0} findings`} 
                color="primary"
              />
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedControl.findings && selectedControl.findings.length > 0 ? (
              <List>
                {selectedControl.findings.map((finding, index) => (
                  <ListItem 
                    key={finding._id || index} 
                    divider
                    onClick={() => {
                      setEditingFinding(finding);
                      setFindingControlName(`${selectedControl.identifier}: ${selectedControl.description}`);
                    }}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <ListItemText
                      primary={finding.evidence || 'No evidence provided'}
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                          <Chip 
                            label={`Status: ${finding.status}`} 
                            size="small" 
                            color={finding.status === 'Open' ? 'warning' : finding.status === 'Resolved' ? 'success' : 'default'}
                          />
                          <Chip 
                            label={`Priority: ${finding.priority}`} 
                            size="small" 
                            color={finding.priority === 'Critical' ? 'error' : finding.priority === 'High' ? 'warning' : 'default'}
                          />
                          {finding.remediationPlan && (
                            <Chip label="Has Remediation Plan" size="small" variant="outlined" />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Alert severity="info">
                No findings for this control. This control is currently compliant.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedControl(null)}>Close</Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => {
                setFindingControlId(selectedControl._id);
                setFindingControlName(`${selectedControl.identifier}: ${selectedControl.description}`);
                setShowAddFinding(true);
                setSelectedControl(null);
              }}
            >
              Add Finding
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <AddFindingForm
        controlId={findingControlId}
        controlName={findingControlName}
        open={showAddFinding}
        onClose={() => setShowAddFinding(false)}
        onSave={async (finding) => {
          await gapAnalysisService.createFinding(finding);
          // Refresh data
          const [controlsData, statsData] = await Promise.all([
            gapAnalysisService.getControls(),
            gapAnalysisService.getStats()
          ]);
          setControls(controlsData);
          setStats(statsData);
          
          // Update the selectedControl with fresh data if dialog is open
          if (selectedControl) {
            const updatedControl = controlsData.find(c => c._id === selectedControl._id);
            if (updatedControl) {
              setSelectedControl(updatedControl);
            }
          }
        }}
      />

      <EditFindingForm
        finding={editingFinding}
        controlName={findingControlName}
        open={!!editingFinding}
        onClose={() => setEditingFinding(null)}
        onSave={async (findingId, updates) => {
          try {
            console.log('Updating finding:', findingId, updates);
            const result = await gapAnalysisService.updateFinding(findingId, updates);
            console.log('Update result:', result);
            
            // Refresh data
            const [controlsData, statsData] = await Promise.all([
              gapAnalysisService.getControls(),
              gapAnalysisService.getStats()
            ]);
            setControls(controlsData);
            setStats(statsData);
            
            // Update the selectedControl with fresh data
            if (selectedControl) {
              const updatedControl = controlsData.find(c => c._id === selectedControl._id);
              if (updatedControl) {
                setSelectedControl(updatedControl);
              }
            }
            
            setEditingFinding(null);
          } catch (error) {
            console.error('Error updating finding:', error);
            throw error; // Re-throw so EditFindingForm can handle it
          }
        }}
      />

      <Dialog
        open={issueDialogOpen}
        onClose={handleCloseRouteIssues}
        fullWidth
        maxWidth="md"
        aria-labelledby="a11y-issues-dialog-title"
      >
        <DialogTitle
          id="a11y-issues-dialog-title"
          sx={{ bgcolor: 'primary.main', color: 'white' }}
        >
          508 Issues 																		
          {selectedRoute ? `																		
            - ${selectedRoute}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {issuesLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography>Loading issues																		</Typography>
            </Box>
          )}

          {issuesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {issuesError}
            </Alert>
          )}

          {!issuesLoading && !issuesError && issues.length === 0 && (
            <Typography>No open 508 issues for this page.</Typography>
          )}

          {!issuesLoading && !issuesError && issues.length > 0 && (
            <Box component="ul" sx={{ pl: 2 }}>
              {issues.map((issue) => (
                <Box
                  component="li"
                  key={issue._id}
                  sx={{ mb: 2 }}
                  aria-label={`${issue.ruleId} issue with ${issue.impact} impact`}
                >
                  <Typography variant="subtitle2">
                    {issue.ruleId}{' '}
                    <Chip
                      label={issue.impact.toUpperCase()}
                      size="small"
                      color={
                        issue.impact === 'critical' || issue.impact === 'serious'
                          ? 'error'
                          : 'warning'
                      }
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {issue.ruleDescription}
                  </Typography>
                  {issue.target && issue.target.length > 0 && (
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      Target: {issue.target.join(' ')}
                    </Typography>
                  )}
                  {issue.helpUrl && (
                    <Typography variant="body2">
                      Help:{' '}
                      <a
                        href={issue.helpUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {issue.helpUrl}
                      </a>
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRouteIssues} color="primary" autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
